import type { IntakeEvidence, IntakeFormPreview } from "@/types/clinician";

const normalized = (value: string) =>
  value.trim().replace(/\s+/g, " ").toLowerCase();

export function intakeSubmissionDate(value: string | null) {
  if (!value || Number.isNaN(Date.parse(value))) return "Date unavailable";
  return new Date(value).toLocaleString();
}

/** The selected source submission is authoritative, never the merged history. */
export function highlightsForForm(form?: IntakeFormPreview): IntakeEvidence[] {
  if (
    !form?.form_id ||
    !["Healthie", "Portal + Healthie"].includes(form.source)
  )
    return [];
  return form.answers
    .filter((answer) => answer.importance !== "context")
    .map((answer) => ({
      ...answer,
      form_name: form.form_name,
      submitted_at: form.submitted_at,
    }))
    .sort(
      (left, right) =>
        Number(right.importance === "safety") -
        Number(left.importance === "safety"),
    );
}

/** Preserve conflicting responses and their provenance; never decide clinically. */
export function IntakeHighlights({
  entries,
  columns = 2,
}: {
  entries: IntakeEvidence[];
  columns?: 1 | 2;
}) {
  const answersByQuestion = new Map<string, Set<string>>();
  for (const entry of entries) {
    const key = normalized(entry.question);
    const answers = answersByQuestion.get(key) ?? new Set<string>();
    answers.add(normalized(entry.answer));
    answersByQuestion.set(key, answers);
  }
  const conflicts = Array.from(answersByQuestion.entries())
    .filter(([, answers]) => answers.size > 1)
    .map(
      ([key]) =>
        entries.find((entry) => normalized(entry.question) === key)!.question,
    );
  const seen = new Set<string>();
  const distinct = entries.filter((entry) => {
    const key = JSON.stringify([
      entry.form_name,
      entry.submitted_at,
      normalized(entry.question),
      normalized(entry.answer),
      entry.importance,
    ]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return (
    <div className="flex flex-col gap-7">
      {conflicts.length > 0 && (
        <div
          role="alert"
          className="p-8 border border-warning/30 bg-warning-wash type-body-sm"
        >
          <p className="font-medium">
            Conflicting intake answers — verify before prescribing
          </p>
          <p className="mt-3">
            Different answers are recorded for: {conflicts.join("; ")}. Review
            the dated sources below. No answer has been chosen automatically.
          </p>
        </div>
      )}
      <div className={`grid gap-7 ${columns === 2 ? "md:grid-cols-2" : ""}`}>
        {distinct.map((entry, index) => (
          <div
            key={`${entry.question}-${index}`}
            className={`p-8 border ${entry.importance === "safety" ? "border-danger/30 bg-danger-wash" : "border-line-subtle bg-sunken"}`}
          >
            <p className="text-11 text-ink-muted mb-5">
              {entry.form_name} · {intakeSubmissionDate(entry.submitted_at)}
            </p>
            <span
              className={`type-label-xs ${entry.importance === "safety" ? "text-danger" : "text-ink-muted"}`}
            >
              {entry.question}
            </span>
            <p className="type-body-sm font-medium mt-3 break-words whitespace-pre-wrap">
              {entry.answer}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
